import { HttpErrorResponse } from "@angular/common/http";
import { computed, inject, Injectable, linkedSignal, signal } from "@angular/core";
import { Router } from "@angular/router";
import {
    Auth0AuthorizationService,
    AuthorizationOrganization,
    MrdmAuthService
} from "@authorization";
import { FHIRMiddlewareApiService, ProjectListResourceData, SubjectUiStatusEnum } from "@fhir-api";

import { INavNode } from "@logex/framework/lg-application";
import { LgTranslateService } from "@logex/framework/lg-localization";

import {
    catchError,
    combineLatest,
    filter,
    map,
    Observable,
    of,
    OperatorFunction,
    shareReplay,
    tap,
    throwError
} from "rxjs";
import { AppDialogService } from "../app-dialog.service";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";

export interface RequestErrorActionStructure {
    [key: number]: {
        lcKey?: string;
        messageVariables?: Record<string, string | number>;
        ignore?: boolean;
        action?: () => any;
    };

    default?: {
        lcKey?: string;
        messageVariables?: Record<string, string | number>;
        action?: () => any;
    };
}

@Injectable({
    providedIn: "root"
})
export class AppStateService {
    private _auth0Service = inject(Auth0AuthorizationService);
    private _authService = inject(MrdmAuthService);
    private _middlewareApiService = inject(FHIRMiddlewareApiService);
    private _router = inject(Router);
    private _translateService = inject(LgTranslateService);
    private _dialogService = inject(AppDialogService);

    private _projectListLoadFailed = signal(false);
    projectListLoadFailed = this._projectListLoadFailed.asReadonly();

    projectList$ = this._middlewareApiService.getProjectList$().pipe(
        this.catchRequestError$(),
        catchError(() => {
            this._projectListLoadFailed.set(true);
            return of<ProjectListResourceData[]>([]);
        }),
        shareReplay(1)
    );
    projectList = toSignal(this.projectList$, { initialValue: [] as ProjectListResourceData[] });

    private _selectedRegistryName = signal<string | null | undefined>(undefined);
    selectedRegistryName = this._selectedRegistryName.asReadonly();
    selectedRegistryName$ = toObservable(this._selectedRegistryName).pipe(
        filter(registryName => registryName !== null && registryName !== undefined)
    );

    private _organizationsLoading = signal(true);
    organizationsLoading = this._organizationsLoading.asReadonly();
    organizations$ = combineLatest([this._authService.userOrganizations$, this.projectList$]).pipe(
        map(([userOrgs, projectList]) => {
            const projectOrgIds = new Set(projectList.map(entry => entry.organizationId));
            return userOrgs.filter(org => projectOrgIds.has(org.organizationId));
        }),
        tap({
            next: () => this._organizationsLoading.set(false),
            error: () => this._organizationsLoading.set(false)
        }),
        shareReplay(1)
    );
    organizations = toSignal(this.organizations$, {
        initialValue: [] as AuthorizationOrganization[]
    });

    private _selectedOrganization = linkedSignal(() => {
        const organizations = this.organizations();
        if (organizations.length === 0) return undefined;
        return organizations.find(org => org.organizationId !== null) ?? undefined;
    });
    selectedOrganization = this._selectedOrganization.asReadonly();
    selectedOrganization$ = toObservable(this._selectedOrganization).pipe(
        filter(organization => organization !== null && organization !== undefined)
    );
    private _navigation = signal<INavNode[]>([]);
    navigation = this._navigation.asReadonly();
    navigation$ = toObservable(this._navigation);
    private _backUrl = "";

    selectedOrganizationId = computed(() => this.selectedOrganization()?.organizationId);
    selectedOrganizationUri = computed(() => this.selectedOrganization()?.mrdmUri);
    selectedOrganizationCicCode = computed(() => this.selectedOrganization()?.cicCode);

    reload(): void {
        window.location.reload();
    }

    selectOrganizationAndRegistry(
        organization: AuthorizationOrganization | number | string | null,
        registryName: string | null
    ): void {
        this._selectedRegistryName.set(registryName);

        const organizations = this.organizations();
        const organizationObject = organizations.find(org => {
            if (typeof organization === "number") {
                return org.organizationId === organization;
            } else if (typeof organization === "string") {
                return org.mrdmUri === organization;
            } else {
                return org.organizationId === organization?.organizationId;
            }
        });

        if (organizationObject) {
            this._selectedOrganization.set(organizationObject);
        } else {
            this.setErrorLc(
                "Organization_not_found",
                {
                    organization:
                        typeof organization === "object"
                            ? (organization?.mrdmUri ?? "")
                            : organization
                },
                () => {
                    this._router.navigateByUrl("/select");
                }
            );
        }
    }

    setErrorLc(
        lcKey: string,
        interpolateParams: Record<string, string | number> | (() => any),
        action?: () => any
    ): void {
        if (typeof interpolateParams === "function" && !action) {
            action = interpolateParams;
            interpolateParams = {};
        }

        this._dialogService.displayErrorMessage(
            this._translateService.translate("APP._Error." + lcKey, interpolateParams),
            action
        );
    }

    setNavigation(navigation: INavNode[]): void {
        this._navigation.set(navigation);
    }

    setBackUrl(url: string): void {
        this._backUrl = url;
    }

    navigateBack({ fallbackUrl }: { fallbackUrl: string }): void {
        const backUrl = this._backUrl;
        this._backUrl = "";
        this._router.navigateByUrl(backUrl || fallbackUrl);
    }

    catchRequestError$<T>(
        requestErrorAction?: RequestErrorActionStructure
    ): OperatorFunction<T, T> {
        return catchError((httpErrorResponse: HttpErrorResponse | Error) => {
            if (!(httpErrorResponse instanceof HttpErrorResponse)) {
                this.setErrorLc(httpErrorResponse.message, {}, requestErrorAction?.default?.action);
                return throwError(() => httpErrorResponse);
            }

            this._handleHttpError(httpErrorResponse, requestErrorAction);
            return throwError(() => httpErrorResponse);
        });
    }

    private _handleHttpError(
        response: HttpErrorResponse,
        requestErrorAction?: RequestErrorActionStructure
    ): void {
        const status = response.status;

        if (status === 401) {
            this.setErrorLc("Unauthorized", () => this._auth0Service.logout());
            return;
        }

        if (status === 403) {
            this.setErrorLc("Forbidden", () => this._auth0Service.logout());
            return;
        }

        if (requestErrorAction?.[status]?.ignore) {
            return;
        }

        const action = requestErrorAction?.[status]?.action ?? requestErrorAction?.default?.action;

        if (status === 400 && this._hasDeletePendingStatus(response.error)) {
            this.setErrorLc("Subject_cannot_be_edited", {}, action);
        } else if (status >= 500) {
            const footer = this._translateService.translate("APP._Error.Request_error_footer");
            this.setErrorLc("Request_5xx_error", { footer }, action);
        } else {
            const lcKey =
                requestErrorAction?.[status]?.lcKey ??
                requestErrorAction?.default?.lcKey ??
                "Request_4xx_error";
            const messageVariables =
                requestErrorAction?.[status]?.messageVariables ??
                requestErrorAction?.default?.messageVariables ??
                {};
            this.setErrorLc(lcKey, messageVariables, action);
        }
    }

    // Temporary hard-coded solution before using translation strings in BE errors
    private _hasDeletePendingStatus(error: unknown): boolean {
        const message =
            typeof error === "string"
                ? error
                : typeof error === "object" &&
                    error !== null &&
                    "message" in error &&
                    typeof error.message === "string"
                  ? error.message
                  : "";

        return message.includes(SubjectUiStatusEnum.DeletePending);
    }
}
